<template>
  <div class="translations-browser">
    <h2>Translation Database Browser</h2>

    <div class="filters">
      <div class="filter-group">
        <label>Language:</label>
        <select v-model="selectedLanguage" @change="fetchTranslations">
          <option value="">All Languages</option>
          <option v-for="lang in languages" :key="lang.code" :value="lang.code">
            {{ lang.name }} ({{ lang.code }})
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Search Key:</label>
        <input
          v-model="searchKey"
          @input="debouncedSearch"
          placeholder="e.g., home.page_title"
        />
      </div>

      <div class="filter-group">
        <label>Key Prefix:</label>
        <select v-model="keyPrefix" @change="fetchTranslations">
          <option value="">All</option>
          <option value="home.">home.*</option>
          <option value="receiving.">receiving.*</option>
          <option value="storage.">storage.*</option>
          <option value="preparation.">preparation.*</option>
          <option value="service.">service.*</option>
          <option value="warewashing.">warewashing.*</option>
          <option value="assessment.">assessment.*</option>
        </select>
      </div>

      <button @click="fetchTranslations" class="btn-refresh">
        🔄 Refresh
      </button>
    </div>

    <div class="stats">
      <p><strong>Total Results:</strong> {{ totalResults }}</p>
      <p v-if="selectedLanguage"><strong>Language:</strong> {{ selectedLanguage }}</p>
    </div>

    <div v-if="loading" class="loading">Loading translations...</div>

    <div v-else-if="translations.length === 0" class="no-results">
      No translations found matching your filters.
    </div>

    <table v-else class="translations-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Language</th>
          <th>Translation Key</th>
          <th>Translation Value</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="trans in translations" :key="trans.id">
          <td>{{ trans.id }}</td>
          <td>{{ trans.languageCode }}</td>
          <td class="key-cell">{{ trans.translationKey }}</td>
          <td class="value-cell">{{ trans.translationValue }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminTranslationsBrowser',

  data() {
    return {
      languages: [],
      translations: [],
      selectedLanguage: '',
      searchKey: '',
      keyPrefix: '',
      loading: false,
      totalResults: 0,
      debounceTimeout: null
    };
  },

  async mounted() {
    await this.fetchLanguages();
    await this.fetchTranslations();
  },

  methods: {
    async fetchLanguages() {
      try {
        const response = await axios.get('/storesafe/api/translations/languages', {
          withCredentials: true
        });
        this.languages = response.data;
      } catch (error) {
        console.error('Error fetching languages:', error);
        alert('Failed to load languages');
      }
    },

    async fetchTranslations() {
      this.loading = true;
      try {
        const params = {
          language: this.selectedLanguage,
          keyPrefix: this.keyPrefix,
          search: this.searchKey
        };

        const response = await axios.get('/storesafe/api/admin/translations/browse', {
          params,
          withCredentials: true
        });

        this.translations = response.data.translations;
        this.totalResults = response.data.total;
      } catch (error) {
        console.error('Error fetching translations:', error);
        alert('Failed to load translations');
      } finally {
        this.loading = false;
      }
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.fetchTranslations();
      }, 500);
    }
  }
};
</script>

<style scoped>
.translations-browser {
  padding: 20px;
}

h2 {
  margin-bottom: 20px;
  color: #333;
}

.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-weight: 600;
  font-size: 14px;
  color: #555;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
}

.btn-refresh {
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.btn-refresh:hover {
  background-color: #0056b3;
}

.stats {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.stats p {
  margin: 5px 0;
  font-size: 14px;
}

.loading,
.no-results {
  padding: 40px;
  text-align: center;
  color: #666;
  font-size: 16px;
}

.translations-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.translations-table th {
  background-color: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  font-size: 14px;
}

.translations-table td {
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  font-size: 13px;
}

.translations-table tr:hover {
  background-color: #f8f9fa;
}

.key-cell {
  font-family: 'Courier New', monospace;
  color: #0066cc;
  font-weight: 500;
}

.value-cell {
  max-width: 400px;
  word-wrap: break-word;
}
</style>
